import ApiService, {SearchParams} from "@/service/ApiService.ts";
import {Address, Customer, PagedList, PhoneNumber} from "@/models/commons.ts";
import {UspsFormData} from "@/service/MailboxRequestService.ts";

export interface MailboxResponse {
    id: string;
    customer: Customer;
    address: Address;
    createdAt: Date;
    modifiedAt: Date;
}

export interface MailboxDetailsResponse {
    id: string;
    mailCenterId: string;
    customerName: string;
    email: string;
    phoneNumber?: PhoneNumber;
    address?: Address;
    mailboxPlanName: string;
    mailboxPlanRecurrence: string;
    couponCode?: string;
    referralCode?: string;
    subscriptionEndDate?: string;
    number: number;
    balance: number;
    archived: boolean;
    isVirtual: boolean;
    uspsFormData?: UspsFormData;
    hasUspsFormUploaded: boolean;
    createdAt: Date;
    modifiedAt: Date;
    hasPaymentMethod: boolean;
    paymentMethodBrand: string;
    paymentMethodLast4: string;
    setupFee: number;
    notaryFee: number;
    activationStatus: number;
    // Pricing details for admin actions
    forwardCharge: number;
    scanPerPageCharge: number;
    shredPerPageCharge: number;
    disposeCharge: number;
    mailStorageChargePerItemPerDayOverGracePeriod: number;
    packageStorageChargePerDayOverGracePeriod: number;
    packagesStorage: number;
    // Customer preferences
    attachScansToEmail: boolean;
    // Navigation (for Previous/Next buttons)
    previous?: string;
    next?: string;
}

export interface SearchMailboxResponse {
    id: string;
    number: number;
    balance: number;
    customerName: string;
    applicantName: string;
    knownNames: string[];
    match: string;
    isVirtual: boolean;
}

export interface NewMailRequest {
    addressCorrectionCharge: boolean;
    arrivedDamaged: boolean;
    type: 'M' | 'P';
    length: number;
    width: number;
    height: number;
    weight: number;
    photo: File;
}

export interface NewMailResponse {
    trackingNumber: string;
}

export interface MailResponse {
    id: string;
    trackingNumber: string;
    createdAt: Date;
    modifiedAt: Date;
    addressCorrectionCharge: boolean;
    arrivedDamaged: boolean;
    type: 'M' | 'P';
    lastEvent?: number;
    weight: number | null | undefined;
    width: number | null | undefined;
    height: number | null | undefined;
    length: number | null | undefined;
    costOfShipping: number | null | undefined;
}

export interface ChargeResponse {
    id: string;
    mailId: string;
    mailTrackingNumber: string;
    amount: number;
    pendingAmount: number;
    description: string;
    approved: boolean;
    paid: boolean;
    createdAt: Date;
    lastPaymentAttemptAt?: Date;
    paymentAttemptCount: number;
    lastPaymentError?: string;
    refundedAmount: number;
    hasRefunds: boolean;
    type: string;
    paymentSources: string[]; // List of payment sources: "Stripe", "Manual", "Balance"
}

export interface TransactionResponse {
    id: string;
    description: string;
    amount: number;
    createdBy: string;
    createdAt: Date;
}

export interface UpdateCustomerDetailsRequest {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: PhoneNumber;
    address?: Address;
}

export interface CustomerDetails {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: PhoneNumber;
    address?: Address;
    createdAt: Date;
    modifiedAt: Date;
}


export interface Applicant {
    firstName: string;
    middleName: string | null;
    lastName: string;
    dateOfBirth: Date;
    phoneNumber: PhoneNumber;
    email: string;
    address: Address;
    idNumber: string;
    idIssuingEntity: string;
    idExpirationDate: Date;
    idType: number;
    idAddressType: number;
}

export interface AuthorizedIndividual {
    id?: string;
    firstName: string;
    middleName: string | null;
    lastName: string;
    phoneNumber: PhoneNumber;
    email: string;
    address: Address;
    idNumber: string;
    idIssuingEntity: string;
    idExpirationDate: Date;
    idType: number;
    idAddressType: number;
}

export interface AdditionalAuthorizedIndividual {
    id?: string;
    firstName: string;
    middleName: string | null;
    lastName: string;
    phoneNumber: PhoneNumber;
    email: string;
    address: Address;
}

export interface TransferAddress {
    phoneNumber: PhoneNumber;
    email: string;
    address: Address;
}

export interface Organization {
    id?: string;
    name: string;
    type: string;
    address: Address;
    phoneNumber: PhoneNumber;
    placeOfRegistration: string;
}

export interface UspsFormPayload {
    applicant: Applicant;
    typeOfService: number;
    isCourtOrderedProtectedIndividual: boolean;
    hasAuthorizedIndividual: boolean;
    authorizedIndividual: AuthorizedIndividual;
    isTransferringToAnotherAddress: boolean;
    transferAddress: TransferAddress;
    organization: Organization;
}

export interface CreateAccountDetails {
    mailCenterId: string;
    mailboxPlanId: string;
    recurrence: string;
    pmbNumber: string;
    isVirtual: boolean;
    customer: {
        firstName: string;
        lastName: string;
        email: string | null;
        knownNames?: string[];
    };
    uspsForm: File | null;
}

export interface UpdatePlanDetailsRequest {
    mailboxPlanId: string;
    recurrence: string;
    couponCode: string | null;
    subscriptionEndDate?: Date | null;
    createCharge: boolean;
}

export interface CreateManualFeeChargeRequest {
    feeType: 'SetupFee' | 'NotaryFee' | 'Other';
    customAmount?: number | null;
    customDescription?: string | null;
}

export interface PaymentDueAccountResponse
{
    id: string;
    number: number;
    customerName: string;
    dueType: string;
    dueAmount: number;
}

export interface PaymentMethodResponse {
    id: string;
    paymentMethodId: string;
    cardBrand?: string;
    cardLast4?: string;
    cardExpMonth?: number;
    cardExpYear?: number;
    cardHolderName?: string;
}

export interface PaymentIntentSession {
    id: string;
    clientSecret: string;
    type: string;
}

export class MailboxService extends ApiService {

    async getMailboxes({page, pageSize, sortOrder, sortField, filter, status}: SearchParams & {status: string} = {
        page: 1,
        pageSize: 10,
        filter: undefined,
        sortField: undefined,
        sortOrder: undefined,
        status: 'active'
    }): Promise<PagedList<MailboxResponse>> {

        const queryParams = new URLSearchParams(`page=${page}&pageSize=${pageSize}`);
        if (sortField && sortOrder) {
            queryParams.append('sortField', sortField);
            queryParams.append('sortOrder', sortOrder);
        }
        if (filter) {
            queryParams.append('filter', filter);
        }
        if (status) {
            queryParams.append('status', status);
        }

        const response = await this.get(`/api/mailbox?${queryParams.toString()}`);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        return await response.json();
    }

    async getMailbox(id: string, navigationParms?: {
        sortField?: string,
        sortOrder?: string,
        filter?: string,
        status: string
    }): Promise<MailboxDetailsResponse> {
        const queryArgs = new URLSearchParams();
        queryArgs.append('status', navigationParms?.status ?? 'active');
        if (navigationParms?.sortField && navigationParms?.sortOrder) {
            queryArgs.append('sortField', navigationParms.sortField);
            queryArgs.append('sortOrder', navigationParms.sortOrder);
        }
        if (navigationParms?.filter) {
            queryArgs.append('filter', navigationParms.filter);
        }
        const response = await this.get(`/api/mailbox/${id}?${queryArgs.toString()}`);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        return await response.json();
    }

    async getProcessMail(): Promise<MailResponse[]> {
        const response = await this.get('/api/mailbox/process-mail');

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        return await response.json();
    }

    async processMail(): Promise<void> {
        const response = await this.post('/api/mailbox/process-mail');

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
    }

    async deletePendingMail(id : string): Promise<void> {
        const response = await this.delete(`/api/mailbox/process-mail/${id}`);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
    }

    async getMails(id: string, params: {availableToPickUp?: boolean} = {}): Promise<MailResponse[]> {
        const queryString = new URLSearchParams();

        for (const key in params) {
            // @ts-ignore
            if (Object.hasOwnProperty.call(params, key) && params[key] !== undefined) {
                // @ts-ignore
                queryString.append(key, params[key]);
            }
        }
        const response = await this.get(`/api/mailbox/${id}/mail?${queryString.toString()}`);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        return await response.json();
    }

    async getCharges(id: string): Promise<ChargeResponse[]> {
        const response = await this.get(`/api/mailbox/${id}/charges`);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        return await response.json();
    }

    async getTransactions(id: string): Promise<TransactionResponse[]> {
        const response = await this.get(`/api/mailbox/${id}/transactions`);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        return await response.json();
    }

    async search(query: string): Promise<SearchMailboxResponse[]> {
        const response = await this.get('/api/mailbox/search?query=' + encodeURIComponent(query));

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        return await response.json();
    }

    async createNewMailRecord(mailboxId: string, request: NewMailRequest): Promise<NewMailResponse> {
        const formData = new FormData();
        // ts-ignore
        const payload = {...request};
        //@ts-ignore
        delete payload.photo;
        formData.append('request', new Blob([JSON.stringify(payload)], {type: 'application/json'}));
        formData.append('photo', request.photo);

        const response = await this.postFormData(`/api/mailbox/${mailboxId}/mail`, formData);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        return await response.json();
    }

    async updateCustomerDetails(mailboxId: string, request: UpdateCustomerDetailsRequest & {idExpirationDate?: Date, mailboxNumber: number}): Promise<void> {
        const response = await this.put(`/api/mailbox/${mailboxId}/customer`, request);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
    }

    /**
     * Archives a mailbox, making its number available for reuse.
     * @param mailboxId The ID of the mailbox to archive
     * @returns Promise that resolves when the mailbox is successfully archived
     */
    async archiveMailbox(mailboxId: string): Promise<void> {
        const response = await this.post(`/api/mailbox/${mailboxId}/archive`);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
    }

    async createVirtualAccount(
        accountDetails: CreateAccountDetails,
        validateOnly: boolean,
    ): Promise<void> {

        // @ts-ignore
        accountDetails.pmbNumber = parseInt(accountDetails.pmbNumber as string);

        const formData = new FormData();

        // @ts-ignore
        delete accountDetails.mailboxPlanId;
        // @ts-ignore
        delete accountDetails.recurrence;

        formData.append('accountDetails', new Blob([JSON.stringify(accountDetails)], {type: 'application/json'}));

        const response = await this.postFormData(`/api/mailbox/?validateOnly=${validateOnly}`, formData);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        return;
    }

    async createAccountWithPreSignedUspsForm(
        accountDetails: CreateAccountDetails,
        validateOnly: boolean,
        uspsForm: File | null = null
    ): Promise<void> {

        const payload = {...accountDetails};
        // @ts-ignore
        payload.pmbNumber = parseInt(payload.pmbNumber as string);
        // @ts-ignore
        delete payload.uspsForm;

        const formData = new FormData();
        formData.append('accountDetails', new Blob([JSON.stringify(payload)], {type: 'application/json'}));


        if (uspsForm) {
            formData.append('uspsFormFile', uspsForm);
        }

        const response = await this.postFormData(`/api/mailbox/?validateOnly=${validateOnly}&usePreSignedForm=true`, formData);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        return;
    }

    async createAccount(
        accountDetails: CreateAccountDetails,
        uspsForm: UspsFormPayload | null,
        validateOnly: boolean,
        courtOrder: File | null = null,
        applicantDocumentId: File | null = null,
        applicantAddressId: File | null = null,
        authorizedIndividualDocumentId: File | null = null,
        authorizedIndividualAddressId: File | null = null,
    ): Promise<void> {

        // @ts-ignore
        accountDetails.pmbNumber = parseInt(accountDetails.pmbNumber as string);

        const formData = new FormData();


        if (!uspsForm!.hasAuthorizedIndividual) {
            // @ts-ignore
            delete uspsForm.authorizedIndividual;
        }
        formData.append('uspsForm', new Blob([JSON.stringify(uspsForm)], {type: 'application/json'}));
        formData.append('accountDetails', new Blob([JSON.stringify(accountDetails)], {type: 'application/json'}));


        if (applicantDocumentId) {
            formData.append('applicantDocumentId', applicantDocumentId);
        }
        if (applicantAddressId) {
            formData.append('applicantAddressId', applicantAddressId);
        }
        if (courtOrder) {
            formData.append('courtOrder', courtOrder);
        }
        if (authorizedIndividualDocumentId) {
            formData.append('authorizedIndividualDocumentId', authorizedIndividualDocumentId);
        }
        if (authorizedIndividualAddressId) {
            formData.append('authorizedIndividualAddressId', authorizedIndividualAddressId);
        }

        const response = await this.postFormData(`/api/mailbox/?validateOnly=${validateOnly}`, formData);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        return;
    }

    /**
     * Updates the plan details for a mailbox including the plan, recurrence, and coupon.
     * @param mailboxId The ID of the mailbox to update
     * @param request The updated plan details
     * @returns Promise that resolves when the plan is successfully updated
     */
    async updatePlanDetails(mailboxId: string, request: UpdatePlanDetailsRequest): Promise<void> {
        const response = await this.put(`/api/mailbox/${mailboxId}/plan`, request);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

    }

    /**
     * Records a payment for a mailbox
     * @param mailboxId The ID of the mailbox
     * @param request Payment details including charge ids, description, and payment method selection
     * @returns Promise that resolves when the payment is successfully recorded
     */
    async recordPayment(mailboxId: string, request: { chargeIds: string[], amount?: number, description: string, usePaymentMethod?: boolean }): Promise<void> {
        const response = await this.post(`/api/mailbox/${mailboxId}/payment`, request);

        if (!response.ok) {
            let error = response.statusText;
            if (response.body) {
                error = await response.json();
            }
            return Promise.reject(error);
        }
    }

    /**
     * Creates a manual fee charge (Setup Fee or Notary Fee) for a mailbox
     * @param mailboxId The ID of the mailbox
     * @param request Fee charge details including fee type, custom amount, and payment method selection
     * @returns Promise that resolves when the fee is successfully charged
     */
    async createManualFeeCharge(mailboxId: string, request: CreateManualFeeChargeRequest): Promise<void> {
        const response = await this.post(`/api/mailbox/${mailboxId}/charges/manual-fee`, request);

        if (!response.ok) {
            let error = response.statusText;
            if (response.body) {
                error = await response.json();
            }
            return Promise.reject(error);
        }
    }

    async getMailboxesMissingData(): Promise<MailboxResponse[]> {
        const response = await this.get('/api/mailbox/missing-data');

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        return await response.json();
    }

    async updateUspsForm(
        mailboxId: string,
        uspsForm: UspsFormPayload | null,
        validateOnly: boolean,
        courtOrder: File | null = null,
        applicantDocumentId: File | null = null,
        applicantAddressId: File | null = null,
        authorizedIndividualDocumentId: File | null = null,
        authorizedIndividualAddressId: File | null = null,
    ): Promise<void> {
        // @ts-ignore
        const formData = new FormData();

        if (!uspsForm!.hasAuthorizedIndividual) {
            // @ts-ignore
            delete uspsForm.authorizedIndividual;
        }
        formData.append('uspsForm', new Blob([JSON.stringify(uspsForm)], {type: 'application/json'}));


        if (applicantDocumentId) {
            formData.append('applicantDocumentId', applicantDocumentId);
        }
        if (applicantAddressId) {
            formData.append('applicantAddressId', applicantAddressId);
        }
        if (courtOrder) {
            formData.append('courtOrder', courtOrder);
        }
        if (authorizedIndividualDocumentId) {
            formData.append('authorizedIndividualDocumentId', authorizedIndividualDocumentId);
        }
        if (authorizedIndividualAddressId) {
            formData.append('authorizedIndividualAddressId', authorizedIndividualAddressId);
        }

        const response = await this.putFormData(`/api/mailbox/${mailboxId}/usps-form?validateOnly=${validateOnly}`, formData);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
    }

    async getMailboxAdditionalIndividualsAndOrganizations(id: string) {
        const response = await this.get(`/api/mailbox/${id}/additional-individuals-and-organizations`);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        return await response.json();
    }

    async createOrganization(mailboxId: string, organization: Organization) {
        const response = await this.post(`/api/mailbox/${mailboxId}/additional-organization`, organization);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
    }

    async updateOrganization(mailboxId: string, organization: Organization) {
        const response = await this.put(`/api/mailbox/${mailboxId}/additional-organization/${organization.id}`, organization);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
    }

    async createIndividual(mailboxId: string, individual: AdditionalAuthorizedIndividual) {
        const response = await this.post(`/api/mailbox/${mailboxId}/additional-individual`, individual);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
    }

    async updateIndividual(mailboxId: string, individual: AdditionalAuthorizedIndividual) {
        const response = await this.put(`/api/mailbox/${mailboxId}/additional-individual/${individual.id}`, individual);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
    }

    async createCustomerKnownName(mailboxId: string, name: string) {
        const response = await this.post(`/api/mailbox/${mailboxId}/known-names`, {name});

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
    }

    async deleteCustomerKnownName(mailboxId: string, name: string) {
        const response = await this.delete(`/api/mailbox/${mailboxId}/known-names?name=${name}`);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
    }

    async listCustomerKnownNames(mailboxId: string) {
        const response = await this.get(`/api/mailbox/${mailboxId}/known-names`);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
        return await response.json();
    }
    
    async markMailAsPickedUp(mailboxId: string, mailId: string): Promise<void> {
        const response = await this.post(`/api/mailbox/${mailboxId}/mail/${mailId}/picked-up`);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
    }

    async markPendingMailAsPickedUp(mailboxId: string): Promise<void> {
        const response = await this.post(`/api/mailbox/${mailboxId}/mark-pending-mail-as-picked-up`);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
    }

    async generateUsps1583Form(id: string) {
        const response = await this.post(`/api/mailbox/${id}/usps-1583-form`);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        return await response.blob();
    }

    async downloadUsps1583Form(id: string) {
        const response = await this.get(`/api/mailbox/${id}/usps-1583-form`);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        return await response.blob();
    }
    
    async getPaymentDueAccounts(): Promise<PaymentDueAccountResponse[]> {
        const response = await this.get(`/api/mailbox/payment-due-accounts`);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        return await response.json();
    }

    async sendAccountSetupEmail(mailboxId: string): Promise<void> {
        const response = await this.post(`/api/mailbox/${mailboxId}/account-setup-email`);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
    }

    async sendResetPasswordEmail(mailboxId: string): Promise<void> {
        const response = await this.post(`/api/mailbox/${mailboxId}/reset-password-email`);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
    }

    async getPaymentMethod(mailboxId: string): Promise<PaymentMethodResponse | null> {
        const response = await this.get(`/api/mailbox/${mailboxId}/payment-method`);
        console.log('fetch payment method response: ', response.status, response.statusText, [...response.headers.entries()], response.type);
        if (response.status === 404) {
            return null;
        }

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        return await response.json();
    }

    async createSetupIntent(mailboxId: string) : Promise<PaymentIntentSession> {
        const response = await this.post(`/api/mailbox/${mailboxId}/setup-intent`);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        return await response.json();
    }

    async cancelSetupIntent(mailboxId: string, setupIntentId: string) : Promise<void> {
        const response = await this.delete(`/api/mailbox/${mailboxId}/setup-intent/${setupIntentId}`);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
    }

    async associatePaymentMethod(mailboxId: string, paymentMethodId: string) {
        const response = await this.put(`/api/mailbox/${mailboxId}/payment-method/${paymentMethodId}`, {});

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
    }

    async removePaymentMethod(mailboxId: string, paymentMethodId: string) {
        const response = await this.delete(`/api/mailbox/${mailboxId}/payment-method/${paymentMethodId}`);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
    }

    async getPendingCharges(mailboxId: string): Promise<ChargeResponse[]> {
        const response = await this.get(`/api/mailbox/${mailboxId}/charges/pending`);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        return await response.json();
    }

    async approveCharges(mailboxId: string, usePaymentMethod: boolean): Promise<void> {
        const response = await this.post(`/api/mailbox/${mailboxId}/charges/approve`, { usePaymentMethod });

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
    }

    async updatePendingCharge(mailboxId: string, chargeId: string, newAmount: number): Promise<void> {
        const response = await this.put(`/api/mailbox/${mailboxId}/charges/${chargeId}`, { newAmount });

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
    }

    async removePendingCharge(mailboxId: string, chargeId: string): Promise<void> {
        const response = await this.delete(`/api/mailbox/${mailboxId}/charges/${chargeId}`);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
    }

    async getMailActionEstimatedCharge(
        mailboxId: string,
        mailId: string,
        actionType: string,
        shippingCost?: number,
        numberOfPages?: number
    ): Promise<number> {
        const params = new URLSearchParams({
            actionType
        });

        if (shippingCost !== undefined) {
            params.append('shippingCost', shippingCost.toString());
        }

        if (numberOfPages !== undefined) {
            params.append('numberOfPages', numberOfPages.toString());
        }

        const response = await this.get(`/api/mailbox/${mailboxId}/mail/${mailId}/estimated-charge?${params.toString()}`);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }

        return await response.json();
    }

    async adminCompleteMailAction(
        mailboxId: string,
        mailId: string,
        request: {
        actionType: string;
        forwardDetails?: {
            address: {
                street: string;
                street2?: string;
                city: string;
                state: string;
                zipCode: string;
                country: string;
            };
            shippingCost: number;
            trackingNumber?: string;
            requestedTracking: boolean;
            requestedMostEconomical: boolean;
            requestedByDate?: Date;
        };
        scanDetails?: {
            numberOfPages: number;
        };
        shredDetails?: {
            numberOfPages: number;
        };
        priceOverride?: number;
        notes?: string;
        scannedFile?: File;
        attachToEmail?: boolean;
    }): Promise<void> {
        const formData = new FormData();

        formData.append('actionType', request.actionType);

        if (request.forwardDetails) {
            formData.append('forwardDetails.address.addressLine1', request.forwardDetails.address.street);
            if (request.forwardDetails.address.street2) {
                formData.append('forwardDetails.address.addressLine2', request.forwardDetails.address.street2);
            }
            formData.append('forwardDetails.address.city', request.forwardDetails.address.city);
            formData.append('forwardDetails.address.state', request.forwardDetails.address.state);
            formData.append('forwardDetails.address.postalCode', request.forwardDetails.address.zipCode);
            formData.append('forwardDetails.address.country', request.forwardDetails.address.country);
            formData.append('forwardDetails.shippingCost', request.forwardDetails.shippingCost.toString());
            if (request.forwardDetails.trackingNumber) {
                formData.append('forwardDetails.trackingNumber', request.forwardDetails.trackingNumber);
            }
            formData.append('forwardDetails.requestedTracking', request.forwardDetails.requestedTracking.toString());
            formData.append('forwardDetails.requestedMostEconomical', request.forwardDetails.requestedMostEconomical.toString());
            if (request.forwardDetails.requestedByDate) {
                formData.append('forwardDetails.requestedByDate', request.forwardDetails.requestedByDate.toISOString());
            }
        }

        if (request.scanDetails) {
            formData.append('scanDetails.numberOfPages', request.scanDetails.numberOfPages.toString());
        }

        if (request.attachToEmail !== undefined && request.attachToEmail !== null) {
            formData.append('attachToEmail', request.attachToEmail.toString());
        }

        if (request.shredDetails) {
            formData.append('shredDetails.numberOfPages', request.shredDetails.numberOfPages.toString());
        }

        if (request.priceOverride !== undefined && request.priceOverride !== null) {
            formData.append('priceOverride', request.priceOverride.toString());
        }

        if (request.notes) {
            formData.append('notes', request.notes);
        }

        if (request.scannedFile) {
            formData.append('scannedDocument', request.scannedFile);
        }

        const response = await this.postFormData(`/api/mailbox/${mailboxId}/mail/${mailId}/process-action`, formData);

        if (!response.ok) {
            const error = await response.json();
            return Promise.reject(error);
        }
    }
}
